"use client";

import type { MotionValue } from "framer-motion";
import type { MouseEvent } from "react";
import { useMotionValue, useSpring, useTransform } from "framer-motion";
import { useRef } from "react";

const SPRING = { stiffness: 100, damping: 20, mass: 0.5 };

// Depth presets for the hero's floating cards: closer cards sit in front of
// the browser plane and swing further with the cursor; far cards sit behind
// it and barely move, so the parallax reads as depth rather than a wobble.
export const HERO_DEPTH = {
	front: { z: 54, range: 13 },
	back: { z: -36, range: 5 },
} as const;

export type HeroDepth = keyof typeof HERO_DEPTH;

/** Springs a motion value's [-0.5, 0.5] input range into [-range, range]. */
export function useParallaxAxis(source: MotionValue<number>, range: number) {
	return useSpring(useTransform(source, [-0.5, 0.5], [-range, range]), SPRING);
}

/**
 * Cursor-driven parallax for the hero visual: tracks pointer position over the
 * stage and derives the glow, cast-shadow and browser-tilt motion values from
 * it. Floating cards derive their own x/y from the same `mx`/`my` via
 * `useParallaxAxis`, so every layer reacts to one shared pointer signal.
 */
export function useHeroMotion() {
	const stageRef = useRef<HTMLDivElement>(null);
	const mx = useMotionValue(0);
	const my = useMotionValue(0);

	const glowX = useParallaxAxis(mx, 7);
	const glowY = useParallaxAxis(my, 7);
	const shadowX = useParallaxAxis(mx, 2);

	// Browser tilt: an Apple-style static pose (rotateY -7deg / rotateX 4deg)
	// with a couple of cursor-driven degrees layered on via the same spring —
	// the interpolation range is centered on the base angle, not on zero.
	const browserX = useParallaxAxis(mx, 3);
	const browserY = useParallaxAxis(my, 3);
	const browserRotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, -5]), SPRING);
	const browserRotateX = useSpring(useTransform(my, [-0.5, 0.5], [6, 2]), SPRING);

	function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
		const rect = stageRef.current?.getBoundingClientRect();
		if (!rect) return;
		mx.set((e.clientX - rect.left) / rect.width - 0.5);
		my.set((e.clientY - rect.top) / rect.height - 0.5);
	}

	function handleMouseLeave() {
		mx.set(0);
		my.set(0);
	}

	return {
		stageRef,
		mx,
		my,
		glowX,
		glowY,
		shadowX,
		browserX,
		browserY,
		browserRotateX,
		browserRotateY,
		handleMouseMove,
		handleMouseLeave,
	};
}
